import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type Fee,
  type FeeManagerConfig,
  type FeeManagerFetchInterface,
  type FeeManagerFetchWriteInterface,
  type GetFeesResponse,
  type SaveConfigResponse,
  type SaveFeesResponse
} from "@dealer-center/core-feemanager-interface";
import { Locale } from "@dealer-center/core-labels";

const queryKeyBase = "core-feemanager";

// GET hooks

export const useFees = ({
  tenantType,
  tenantId,
  disabled,
  basePath
}: FeeManagerFetchInterface & { basePath: string }) => {
  return useQuery({
    queryKey: [queryKeyBase, "fees", tenantType, tenantId],
    queryFn: () => getAllFees(basePath, tenantType, tenantId),
    enabled: !disabled && !!tenantId,
    retry: 1,
    staleTime: 1000 * 60 // 1 minute
  });
};

export const useVersion = ({ basePath }: { basePath: string }) => {
  return useQuery({
    queryKey: [queryKeyBase, "version"],
    queryFn: () => getVersion(basePath),
    retry: 1,
    staleTime: Infinity
  });
};

export const useGetConfig = ({
  tenantType,
  tenantId,
  disabled,
  basePath
}: FeeManagerFetchInterface & { basePath: string }) => {
  return useQuery({
    queryKey: [queryKeyBase, "config", tenantType, tenantId],
    queryFn: () => getConfig(basePath, tenantType, tenantId),
    enabled: !disabled && !!tenantId,
    retry: 1,
    staleTime: 1000 * 60 // 1 minute
  });
};

export const useSaveConfig = ({
  tenantType,
  tenantId,
  afterSuccess,
  basePath
}: FeeManagerFetchWriteInterface & { basePath: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      config,
      tenantUser
    }: {
      config: FeeManagerConfig;
      tenantUser: string;
    }) => saveConfig(basePath, tenantType, tenantId, config, tenantUser),
    onSuccess: data => {
      if (data.success) {
        queryClient.setQueryData<Record<string, unknown>>(
          [queryKeyBase, "config", tenantType, tenantId],
          data.config as Record<string, unknown>
        );
      }

      if (typeof afterSuccess === "function") {
        afterSuccess(data);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useSaveConfig] mutation failed:", error);
    }
  });
};

export const useSaveFees = ({
  tenantType,
  tenantId,
  afterSuccess,
  basePath
}: FeeManagerFetchWriteInterface & { basePath: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fees, tenantUser }: { fees: Fee[]; tenantUser: string }) =>
      saveAllFees(basePath, tenantType, tenantId, fees, tenantUser),
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase, "fees", tenantType, tenantId]
      });

      if (typeof afterSuccess === "function") {
        afterSuccess(data);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useSaveFees] mutation failed:", error);
    }
  });
};

// API functions

const getConfig = async (
  basePath: string,
  tenantType: string,
  tenantId: string
): Promise<Record<string, unknown>> => {
  const res = await fetch(
    `${basePath}/config?tenantType=${tenantType}&tenantId=${tenantId}`,
    { headers: { accept: "application/json" } }
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const getVersion = async (basePath: string): Promise<{ version: string }> => {
  const res = await fetch(`${basePath}/version`);
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const getAllFees = async (
  basePath: string,
  tenantType: string,
  tenantId: string
): Promise<GetFeesResponse> => {
  const locale = Locale.getLocale();
  const res = await fetch(
    `${basePath}/fees?tenantType=${tenantType}&tenantId=${tenantId}`,
    {
      headers: {
        "Accept-Language": locale
      }
    }
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const saveConfig = async (
  basePath: string,
  tenantType: string,
  tenantId: string,
  config: FeeManagerConfig,
  tenantUser: string
): Promise<SaveConfigResponse> => {
  const res = await fetch(`${basePath}/config`, {
    body: JSON.stringify({ tenantType, tenantId, config, tenantUser }),
    headers: { "Content-Type": "application/json" },
    method: "POST"
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const saveAllFees = async (
  basePath: string,
  tenantType: string,
  tenantId: string,
  fees: Fee[],
  tenantUser: string
): Promise<SaveFeesResponse> => {
  const locale = Locale.getLocale();
  const res = await fetch(`${basePath}/fees`, {
    body: JSON.stringify({ tenantType, tenantId, fees, tenantUser }),
    headers: {
      "Accept-Language": locale,
      "Content-Type": "application/json"
    },
    method: "POST"
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};
